/**
 * FlatArrayField - Custom Backstage Scaffolder Field Extension
 *
 * A flattened array field renderer that eliminates nested containers,
 * providing a cleaner UI for complex array structures.
 *
 * Usage in template.yaml:
 *   ui:field: FlatArrayField
 *   ui:options:
 *     itemLayout: 'grid' | 'stacked' | 'inline'   # Default: 'stacked'
 *     collapsible: true | false                   # Default: false
 *     showItemBorders: true | false               # Default: true (subtle dividers)
 *     columns: 2 | 3 | 4                          # For grid layout, default: 2
 *     addButtonText: 'Add Item'                   # Custom add button text
 *     itemTitle: 'Item'                           # Title for each item
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Chip,
  makeStyles,
  Theme,
  createStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

// Import custom field extensions for passthrough rendering
import { GithubTeamPicker } from './GithubTeamPicker';
import { GithubRepoPicker } from './GithubRepoPicker';
import { GithubFilePicker } from './GithubFilePicker';
import { GithubRepoVariablePicker } from './GithubRepoVariablePicker';

// Registry of custom field components that can be rendered inline
type CustomFieldComponent = React.ComponentType<FieldExtensionComponentProps<any, any>>;
const customFieldRegistry: Record<string, CustomFieldComponent> = {
  GithubTeamPicker,
  GithubRepoPicker,
  GithubFilePicker,
  GithubRepoVariablePicker,
};

export interface FlatArrayFieldOptions {
  itemLayout?: 'grid' | 'stacked' | 'inline';
  collapsible?: boolean;
  showItemBorders?: boolean;
  columns?: 2 | 3 | 4;
  addButtonText?: string;
  itemTitle?: string;
  addable?: boolean;
  removable?: boolean;
  orderable?: boolean;
  [key: string]: string | number | boolean | object | any[] | null | undefined;
}

interface ItemSchema {
  type?: string;
  title?: string;
  description?: string;
  properties?: Record<string, PropertySchema>;
  required?: string[];
  enum?: string[];
  enumNames?: string[];
  default?: any;
  items?: ItemSchema;
  pattern?: string;
}

interface PropertySchema {
  type?: string;
  title?: string;
  description?: string;
  default?: any;
  enum?: string[];
  enumNames?: string[];
  items?: ItemSchema;
  properties?: Record<string, PropertySchema>;
  required?: string[];
  pattern?: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1),
    },
    itemContainer: {
      position: 'relative',
      padding: theme.spacing(2),
      marginBottom: theme.spacing(1),
      backgroundColor: theme.palette.type === 'dark' 
        ? 'rgba(255, 255, 255, 0.03)' 
        : 'rgba(0, 0, 0, 0.02)',
      borderRadius: theme.shape.borderRadius,
      '&:hover': {
        backgroundColor: theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.03)',
      },
    },
    itemContainerNoBorder: {
      position: 'relative',
      padding: theme.spacing(1, 0),
      marginBottom: theme.spacing(1),
    },
    itemHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1),
    },
    itemNumber: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      color: theme.palette.text.secondary,
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    itemActions: {
      display: 'flex',
      gap: theme.spacing(0.5),
    },
    fieldRow: {
      marginBottom: theme.spacing(1.5),
    },
    inlineFields: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(2),
      '& > *': {
        flex: '1 1 200px',
        minWidth: 150,
      },
    },
    gridFields: {
      display: 'grid',
      gap: theme.spacing(2),
    },
    nestedArray: {
      marginTop: theme.spacing(1),
      marginLeft: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      borderLeft: `2px solid ${theme.palette.divider}`,
    },
    nestedArrayHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1),
    },
    nestedArrayTitle: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: theme.palette.text.secondary,
    },
    chipContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(0.5),
      marginTop: theme.spacing(0.5),
    },
    addButton: {
      marginTop: theme.spacing(1),
    },
    smallAddButton: {
      padding: theme.spacing(0.25, 1),
      fontSize: '0.75rem',
      minWidth: 'auto',
    },
    emptyState: {
      padding: theme.spacing(2),
      textAlign: 'center',
      color: theme.palette.text.secondary,
      fontStyle: 'italic',
    },
    accordion: {
      marginBottom: theme.spacing(1),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      '&:before': {
        display: 'none',
      },
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(255, 255, 255, 0.02)'
        : 'rgba(0, 0, 0, 0.01)',
      '&.Mui-expanded': {
        margin: `0 0 ${theme.spacing(1)}px 0`,
        backgroundColor: theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.04)'
          : 'rgba(0, 0, 0, 0.02)',
      },
    },
    accordionSummary: {
      padding: theme.spacing(0, 2),
      minHeight: '48px !important',
      borderRadius: theme.shape.borderRadius,
      '& .MuiAccordionSummary-content': {
        margin: '12px 0 !important',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      },
      '& .MuiAccordionSummary-expandIcon': {
        marginRight: theme.spacing(-1),
      },
      '&:hover': {
        backgroundColor: theme.palette.type === 'dark'
          ? 'rgba(255, 255, 255, 0.05)'
          : 'rgba(0, 0, 0, 0.04)',
      },
    },
    accordionSummaryContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      paddingRight: theme.spacing(1),
    },
    accordionSummaryLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    accordionDetails: {
      padding: theme.spacing(2),
      paddingTop: theme.spacing(2),
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    removeButtonInHeader: {
      marginLeft: 'auto',
      color: theme.palette.error.main,
      opacity: 0.7,
      '&:hover': {
        opacity: 1,
        backgroundColor: theme.palette.type === 'dark'
          ? 'rgba(244, 67, 54, 0.1)'
          : 'rgba(244, 67, 54, 0.08)',
      },
    },
    keyValueList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    keyValueRow: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    keyValueInput: {
      flex: 1,
      minWidth: 80,
      '& .MuiOutlinedInput-input': {
        padding: '8px 12px',
      },
    },
    keyValueSeparator: {
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    keyValueDelete: {
      color: theme.palette.text.secondary,
      opacity: 0.6,
      '&:hover': {
        opacity: 1,
        color: theme.palette.error.main,
      },
    },
    editableChip: {
      height: 32,
      '& .MuiChip-label': {
        display: 'flex',
        alignItems: 'center',
        padding: '0 8px',
      },
    },
    chipInput: {
      fontSize: '0.875rem',
      '& input': {
        padding: '4px 0',
        height: 'auto',
        lineHeight: 1.5,
        minWidth: 60,
        textAlign: 'left',
      },
    },
    compactItemRow: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(1),
    },
    compactFieldText: {
      flex: 1,
      '& .MuiOutlinedInput-root': {
        height: 56,
      },
    },
    compactFieldSelect: {
      flex: 1,
      marginTop: 15, // Align with custom fields that have floating labels
    },
    compactFieldCustom: {
      flex: 1,
      minWidth: 0,
      '& .MuiFormControl-root': {
        margin: '0 !important',
        width: '100%',
      },
      '& .MuiFormControl-marginNormal': {
        marginTop: '0 !important',
        marginBottom: '0 !important',
      },
    },
    compactItemDelete: {
      marginTop: 27.5, // Align with fields
      color: theme.palette.text.secondary,
      opacity: 0.5,
      '&:hover': {
        opacity: 1,
        color: theme.palette.error.main,
      },
    },
    divider: {
      margin: theme.spacing(1, 0),
    },
    itemsContainer: {
      paddingTop: theme.spacing(2),
    },
  }),
);

// Helper to get a display value for an item (for collapsed view)
const getItemDisplayValue = (item: any, _schema: ItemSchema): string => {
  if (typeof item === 'string') return item;
  if (typeof item === 'number') return String(item);
  if (typeof item === 'boolean') return item ? 'Yes' : 'No';
  if (item && typeof item === 'object') {
    // Try to find a "name" or "title" property
    if (item.name) return item.name;
    if (item.title) return item.title;
    if (item.key) return item.key;
    // Return first string property
    const firstStringKey = Object.keys(item).find(k => typeof item[k] === 'string');
    if (firstStringKey) return item[firstStringKey];
    return `Item`;
  }
  return 'Item';
};

export const FlatArrayField = (
  props: FieldExtensionComponentProps<any[], FlatArrayFieldOptions>,
) => {
  const {
    onChange,
    rawErrors,
    required,
    formData = [],
    formContext,
    uiSchema,
    schema,
  } = props;

  const classes = useStyles();
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const options = uiSchema?.['ui:options'] || {};
  const itemLayout = options.itemLayout || 'stacked';
  const collapsible = options.collapsible ?? false;
  const showItemBorders = options.showItemBorders ?? true;
  const columns = options.columns || 2;
  const addButtonText = options.addButtonText || 'Add';
  const schemaItems = schema.items as ItemSchema | undefined;
  const itemTitle = options.itemTitle || schemaItems?.title || 'Item';
  const addable = options.addable ?? true;
  const removable = options.removable ?? true;

  const itemSchema = schema.items as ItemSchema | undefined;
  const items = Array.isArray(formData) ? formData : [];

  const handleAddItem = useCallback(() => {
    const newItem = getDefaultValue(itemSchema);
    onChange([...items, newItem]);
    if (collapsible) {
      setExpandedItems(prev => new Set([...prev, items.length]));
    }
  }, [items, itemSchema, onChange, collapsible]);

  const handleRemoveItem = useCallback((index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(index);
      // Adjust indices for items after the removed one
      const adjusted = new Set<number>();
      newSet.forEach(i => adjusted.add(i > index ? i - 1 : i));
      return adjusted;
    });
  }, [items, onChange]);

  const handleItemChange = useCallback((index: number, newValue: any) => {
    const newItems = [...items];
    newItems[index] = newValue;
    onChange(newItems);
  }, [items, onChange]);

  const toggleExpanded = useCallback((index: number) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  }, []);

  const renderPrimitiveField = (
    value: any,
    propSchema: PropertySchema,
    onFieldChange: (value: any) => void,
    fieldKey: string,
  ) => {
    // Check if this field has a custom ui:field that we can render
    const customFieldName = itemsUiSchema?.[fieldKey]?.['ui:field'];
    const CustomFieldComponent = customFieldName ? customFieldRegistry[customFieldName] : null;
    
    if (CustomFieldComponent) {
      // Render the custom field component with appropriate props
      const fieldUiSchema = itemsUiSchema?.[fieldKey] || {};
      return (
        <CustomFieldComponent
          onChange={onFieldChange}
          rawErrors={[]}
          required={false}
          formData={value}
          uiSchema={fieldUiSchema}
          schema={propSchema as any}
          formContext={formContext}
          idSchema={{ $id: `${fieldKey}` } as any}
          name={fieldKey}
          disabled={false}
          readonly={false}
          hideError={false}
          autofocus={false}
          placeholder={propSchema.title || fieldKey}
          registry={{} as any}
          onBlur={() => {}}
          onFocus={() => {}}
        />
      );
    }

    // Enum field - render as select
    if (propSchema.enum) {
      return (
        <FormControl fullWidth size="small" variant="outlined">
          <InputLabel>{propSchema.title || fieldKey}</InputLabel>
          <Select
            value={value ?? ''}
            onChange={(e) => onFieldChange(e.target.value)}
            label={propSchema.title || fieldKey}
          >
            {propSchema.enum.map((option, i) => (
              <MenuItem key={option} value={option}>
                {propSchema.enumNames?.[i] || option}
              </MenuItem>
            ))}
          </Select>
          {propSchema.description && (
            <FormHelperText>{propSchema.description}</FormHelperText>
          )}
        </FormControl>
      );
    }

    // Boolean field - render as checkbox
    if (propSchema.type === 'boolean') {
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={value ?? false}
              onChange={(e) => onFieldChange(e.target.checked)}
              size="small"
            />
          }
          label={
            <Box>
              <Typography variant="body2">{propSchema.title || fieldKey}</Typography>
              {propSchema.description && (
                <Typography variant="caption" color="textSecondary">
                  {propSchema.description}
                </Typography>
              )}
            </Box>
          }
        />
      );
    }

    // Number field
    if (propSchema.type === 'number' || propSchema.type === 'integer') {
      return (
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          type="number"
          label={propSchema.title || fieldKey}
          value={value ?? ''}
          onChange={(e) => onFieldChange(Number(e.target.value))}
          helperText={propSchema.description}
        />
      );
    }

    // Default: string field
    return (
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        label={propSchema.title || fieldKey}
        value={value ?? ''}
        onChange={(e) => onFieldChange(e.target.value)}
        helperText={propSchema.description}
      />
    );
  };

  const renderNestedArray = (
    arrayValue: any[],
    arraySchema: PropertySchema,
    onArrayChange: (value: any[]) => void,
    title: string,
  ) => {
    const nestedItems = Array.isArray(arrayValue) ? arrayValue : [];
    const nestedItemSchema = arraySchema.items;
    const isSimpleArray = nestedItemSchema?.type === 'string' || 
                          nestedItemSchema?.type === 'number';

    const handleAddNestedItem = () => {
      const newItem = getDefaultValue(nestedItemSchema);
      onArrayChange([...nestedItems, newItem]);
    };

    const handleRemoveNestedItem = (index: number) => {
      onArrayChange(nestedItems.filter((_, i) => i !== index));
    };

    const handleNestedItemChange = (index: number, value: any) => {
      const newItems = [...nestedItems];
      newItems[index] = value;
      onArrayChange(newItems);
    };

    // Simple string/number arrays render as chips with input
    if (isSimpleArray) {
      return (
        <Box className={classes.nestedArray}>
          <Box className={classes.nestedArrayHeader}>
            <Typography className={classes.nestedArrayTitle}>
              {title}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddNestedItem}
              className={classes.smallAddButton}
            >
              Add
            </Button>
          </Box>
          {nestedItems.length === 0 ? (
            <Typography variant="caption" color="textSecondary">
              No items
            </Typography>
          ) : (
            <Box className={classes.chipContainer}>
              {nestedItems.map((item, idx) => (
                <Chip
                  key={idx}
                  label={
                    <TextField
                      value={item ?? ''}
                      onChange={(e) => handleNestedItemChange(idx, e.target.value)}
                      size="small"
                      variant="standard"
                      InputProps={{
                        disableUnderline: true,
                        className: classes.chipInput,
                      }}
                    />
                  }
                  onDelete={() => handleRemoveNestedItem(idx)}
                  className={classes.editableChip}
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Box>
      );
    }

    // Check if this is a simple two-property object (common pattern for key-value, name-permission, etc.)
    const propertyKeys = nestedItemSchema?.properties ? Object.keys(nestedItemSchema.properties) : [];
    const isTwoFieldObject = nestedItemSchema?.type === 'object' && 
      nestedItemSchema.properties &&
      propertyKeys.length === 2 &&
      propertyKeys.every(key => {
        const prop = nestedItemSchema.properties![key];
        return prop.type !== 'array' && prop.type !== 'object';
      });

    // Render two-field objects in a compact inline format
    if (isTwoFieldObject) {
      const [firstKey, secondKey] = propertyKeys;
      const firstProp = nestedItemSchema.properties![firstKey];
      const secondProp = nestedItemSchema.properties![secondKey];

      const renderInlineField = (
        propSchema: PropertySchema,
        propKey: string,
        value: any,
        onFieldChange: (value: any) => void,
      ) => {
        // Enum field - render as select
        if (propSchema.enum) {
          return (
            <FormControl size="small" variant="outlined" className={classes.keyValueInput}>
              <Select
                value={value ?? propSchema.default ?? ''}
                onChange={(e) => onFieldChange(e.target.value)}
                displayEmpty
              >
                {propSchema.enum.map((option, i) => (
                  <MenuItem key={option} value={option}>
                    {propSchema.enumNames?.[i] || option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }

        // Default: text field
        return (
          <TextField
            size="small"
            variant="outlined"
            placeholder={propSchema.title || propKey}
            value={value ?? ''}
            onChange={(e) => onFieldChange(e.target.value)}
            className={classes.keyValueInput}
          />
        );
      };

      return (
        <Box className={classes.nestedArray}>
          <Box className={classes.nestedArrayHeader}>
            <Typography className={classes.nestedArrayTitle}>
              {title}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddNestedItem}
              className={classes.smallAddButton}
            >
              Add
            </Button>
          </Box>
          {nestedItems.length === 0 ? (
            <Typography variant="caption" color="textSecondary">
              No items
            </Typography>
          ) : (
            <Box className={classes.keyValueList}>
              {nestedItems.map((nestedItem, idx) => (
                <Box key={idx} className={classes.keyValueRow}>
                  {renderInlineField(
                    firstProp,
                    firstKey,
                    nestedItem?.[firstKey],
                    (val) => handleNestedItemChange(idx, { ...nestedItem, [firstKey]: val }),
                  )}
                  {renderInlineField(
                    secondProp,
                    secondKey,
                    nestedItem?.[secondKey],
                    (val) => handleNestedItemChange(idx, { ...nestedItem, [secondKey]: val }),
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveNestedItem(idx)}
                    className={classes.keyValueDelete}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    // Complex nested arrays (objects)
    return (
      <Box className={classes.nestedArray}>
        <Box className={classes.nestedArrayHeader}>
          <Typography className={classes.nestedArrayTitle}>
            {title}
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddNestedItem}
            className={classes.smallAddButton}
          >
            Add
          </Button>
        </Box>
        {nestedItems.length === 0 ? (
          <Typography variant="caption" color="textSecondary">
            No items
          </Typography>
        ) : (
          nestedItems.map((nestedItem, idx) => (
            <Box key={idx} style={{ marginBottom: 8 }}>
              {renderObjectFields(
                nestedItem,
                nestedItemSchema || {},
                (value) => handleNestedItemChange(idx, value),
                idx,
              )}
              <IconButton
                size="small"
                onClick={() => handleRemoveNestedItem(idx)}
                style={{ marginLeft: 8 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))
        )}
      </Box>
    );
  };

  const renderObjectFields = (
    itemValue: any,
    objSchema: ItemSchema,
    onItemChange: (value: any) => void,
    itemIndex: number,
  ) => {
    const properties = objSchema.properties || {};
    const propertyKeys = Object.keys(properties);

    const layoutClass = itemLayout === 'grid'
      ? classes.gridFields
      : itemLayout === 'inline'
        ? classes.inlineFields
        : undefined;

    const gridStyle = itemLayout === 'grid'
      ? { gridTemplateColumns: `repeat(${columns}, 1fr)` }
      : undefined;

    return (
      <Box className={layoutClass} style={gridStyle}>
        {propertyKeys.map((propKey) => {
          const propSchema = properties[propKey];
          const propValue = itemValue?.[propKey];

          // Nested array
          if (propSchema.type === 'array') {
            return (
              <Box key={propKey} className={classes.fieldRow}>
                {renderNestedArray(
                  propValue,
                  propSchema,
                  (value) => onItemChange({ ...itemValue, [propKey]: value }),
                  propSchema.title || propKey,
                )}
              </Box>
            );
          }

          // Nested object (non-array)
          if (propSchema.type === 'object' && propSchema.properties) {
            return (
              <Box key={propKey} className={classes.fieldRow}>
                <Typography variant="subtitle2" gutterBottom>
                  {propSchema.title || propKey}
                </Typography>
                {renderObjectFields(
                  propValue || {},
                  propSchema,
                  (value) => onItemChange({ ...itemValue, [propKey]: value }),
                  itemIndex,
                )}
              </Box>
            );
          }

          // Primitive field
          return (
            <Box key={propKey} className={classes.fieldRow}>
              {renderPrimitiveField(
                propValue,
                propSchema,
                (value) => onItemChange({ ...itemValue, [propKey]: value }),
                propKey,
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  // Check if item schema is a simple two-field object
  const itemPropertyKeys = itemSchema?.properties ? Object.keys(itemSchema.properties) : [];
  const itemsUiSchema = uiSchema?.items as Record<string, any> | undefined;
  
  // Check if custom fields are used AND if they're all in our registry (can be rendered inline)
  const customFieldsUsed = itemPropertyKeys
    .map(key => itemsUiSchema?.[key]?.['ui:field'])
    .filter(Boolean);
  const allCustomFieldsSupported = customFieldsUsed.every(
    fieldName => fieldName in customFieldRegistry
  );
  
  const isSimpleTwoFieldItem = itemSchema?.type === 'object' && 
    itemSchema.properties &&
    itemPropertyKeys.length === 2 &&
    allCustomFieldsSupported &&
    itemPropertyKeys.every(key => {
      const prop = itemSchema.properties![key];
      return prop.type !== 'array' && prop.type !== 'object';
    });

  const renderItem = (item: any, index: number) => {
    const containerClass = showItemBorders
      ? classes.itemContainer
      : classes.itemContainerNoBorder;

    // Compact inline rendering for simple two-field objects when not collapsible
    if (isSimpleTwoFieldItem && !collapsible) {
      const [firstKey, secondKey] = itemPropertyKeys;
      const firstProp = itemSchema!.properties![firstKey];
      const secondProp = itemSchema!.properties![secondKey];

      const renderCompactField = (
        propSchema: PropertySchema,
        propKey: string,
        value: any,
        onFieldChange: (value: any) => void,
      ) => {
        // Check if this field has a custom ui:field that we can render
        const customFieldName = itemsUiSchema?.[propKey]?.['ui:field'];
        const CustomFieldComponent = customFieldName ? customFieldRegistry[customFieldName] : null;
        
        if (CustomFieldComponent) {
          // Render the custom field component with appropriate props
          const fieldUiSchema = itemsUiSchema?.[propKey] || {};
          return (
            <Box className={classes.compactFieldCustom}>
              <CustomFieldComponent
                onChange={onFieldChange}
                rawErrors={[]}
                required={false}
                formData={value}
                uiSchema={fieldUiSchema}
                schema={propSchema as any}
                formContext={formContext}
                idSchema={{ $id: `${propKey}` } as any}
                name={propKey}
                disabled={false}
                readonly={false}
                hideError={false}
                autofocus={false}
                placeholder={propSchema.title || propKey}
                registry={{} as any}
                onBlur={() => {}}
                onFocus={() => {}}
              />
            </Box>
          );
        }

        // Enum field - render as select
        if (propSchema.enum) {
          return (
            <FormControl variant="outlined" className={classes.compactFieldSelect}>
              <Select
                value={value ?? propSchema.default ?? ''}
                onChange={(e) => onFieldChange(e.target.value)}
                displayEmpty
                style={{ height: 56 }}
              >
                {propSchema.enum.map((option, i) => (
                  <MenuItem key={option} value={option}>
                    {propSchema.enumNames?.[i] || option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }

        // Default: text field
        return (
          <TextField
            size="small"
            variant="outlined"
            placeholder={propSchema.title || propKey}
            value={value ?? ''}
            onChange={(e) => onFieldChange(e.target.value)}
            className={classes.compactFieldText}
          />
        );
      };

      return (
        <Box key={index} className={classes.compactItemRow}>
          {renderCompactField(
            firstProp,
            firstKey,
            item?.[firstKey],
            (val) => handleItemChange(index, { ...item, [firstKey]: val }),
          )}
          {renderCompactField(
            secondProp,
            secondKey,
            item?.[secondKey],
            (val) => handleItemChange(index, { ...item, [secondKey]: val }),
          )}
          {removable && (
            <IconButton
              size="small"
              onClick={() => handleRemoveItem(index)}
              className={classes.compactItemDelete}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      );
    }

    const itemContent = (
      <>
        <Box className={classes.itemHeader}>
          <Box className={classes.itemNumber}>
            <DragIndicatorIcon fontSize="small" style={{ opacity: 0.3 }} />
            <span>{itemTitle} {index + 1}</span>
            {collapsible && !expandedItems.has(index) && (
              <Typography variant="caption" color="textSecondary" style={{ marginLeft: 8 }}>
                — {getItemDisplayValue(item, itemSchema || {})}
              </Typography>
            )}
          </Box>
          <Box className={classes.itemActions}>
            {removable && (
              <IconButton
                size="small"
                onClick={() => handleRemoveItem(index)}
                aria-label="Remove item"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Render based on item type */}
        {itemSchema?.type === 'object' && itemSchema.properties ? (
          renderObjectFields(
            item,
            itemSchema,
            (value) => handleItemChange(index, value),
            index,
          )
        ) : itemSchema?.type === 'array' ? (
          renderNestedArray(
            item,
            itemSchema,
            (value) => handleItemChange(index, value),
            itemSchema.title || 'Items',
          )
        ) : (
          // Simple primitive item
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={item ?? ''}
            onChange={(e) => handleItemChange(index, e.target.value)}
          />
        )}
      </>
    );

    if (collapsible) {
      return (
        <Accordion
          key={index}
          expanded={expandedItems.has(index)}
          onChange={() => toggleExpanded(index)}
          className={classes.accordion}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
          >
            <Box className={classes.accordionSummaryContent}>
              <Box className={classes.accordionSummaryLeft}>
                <Typography variant="subtitle2">
                  {getItemDisplayValue(item, itemSchema || {}) || `${itemTitle} ${index + 1}`}
                </Typography>
              </Box>
              {removable && (
                <IconButton
                  size="small"
                  className={classes.removeButtonInHeader}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveItem(index);
                  }}
                  aria-label="Remove item"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            <Box style={{ width: '100%' }}>
              {itemSchema?.type === 'object' && itemSchema.properties ? (
                renderObjectFields(
                  item,
                  itemSchema,
                  (value) => handleItemChange(index, value),
                  index,
                )
              ) : (
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={item ?? ''}
                  onChange={(e) => handleItemChange(index, e.target.value)}
                />
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      );
    }

    return (
      <Box key={index} className={containerClass}>
        {itemContent}
      </Box>
    );
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography variant="subtitle1">
          {schema.title}
          {required && <span style={{ color: 'red' }}> *</span>}
        </Typography>
      </Box>

      {schema.description && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {schema.description}
        </Typography>
      )}

      <Box className={classes.itemsContainer}>
        {items.length === 0 ? (
          <Box className={classes.emptyState}>
            No items yet. Click "{addButtonText}" to add one.
          </Box>
        ) : (
          items.map((item, index) => renderItem(item, index))
        )}
      </Box>

      {addable && (
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
          className={classes.addButton}
        >
          {addButtonText} {itemTitle}
        </Button>
      )}

      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText error>
          {rawErrors.join(', ')}
        </FormHelperText>
      )}
    </Box>
  );
};

// Helper to get default value based on schema
function getDefaultValue(schema: ItemSchema | undefined): any {
  if (!schema) return '';

  if (schema.default !== undefined) {
    return schema.default;
  }

  switch (schema.type) {
    case 'object':
      if (schema.properties) {
        const obj: Record<string, any> = {};
        Object.entries(schema.properties).forEach(([key, propSchema]) => {
          obj[key] = getDefaultValue(propSchema as ItemSchema);
        });
        return obj;
      }
      return {};
    case 'array':
      return [];
    case 'boolean':
      return false;
    case 'number':
    case 'integer':
      return 0;
    case 'string':
    default:
      return '';
  }
}

export default FlatArrayField;

